import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    Button,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { adminInstance } from '../../../utils/axios';

// Simple component without complex logic
function AdminExamsSimple() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [examList, setExamList] = useState([]);

    // Simple fetch function
    useEffect(() => {
        async function fetchData() {
            try {
                setLoading(true);
                const response = await adminInstance.get('/api/admin/exams');
                // Use response.data directly as the backend sends the array
                //console.log(response.data);
                setExamList(response.data || []); 
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to load exams');
                setExamList([]);
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, []);

    // Simple delete handler
    const handleDelete = async (id) => {
        if (!window.confirm('Delete this exam?')) return;
        
        try {
            await adminInstance.delete(`/api/admin/exams/${id}`);
            setExamList(prev => prev.filter(exam => exam._id !== id));
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete exam');
        }
    };

    // Loading state
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" m={5}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4">Manage Exams</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/admin/exams/create')}
                >
                    Create Exam
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{mb: 3}}>
                    {error}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Title</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Questions</TableCell>
                            <TableCell>Subscription Fee</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {examList.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No exams found
                                </TableCell>
                            </TableRow>
                        ) : (
                            // Safe mapping - we know examList is an array
                            examList.map(exam => (
                                <TableRow key={exam._id}>
                                    <TableCell>{exam.title || 'Untitled'}</TableCell>
                                    <TableCell>{exam.category || 'N/A'}</TableCell>
                                    <TableCell>
                                        {exam.questions?.length || 0}/{exam.numberOfQuestions || 0}
                                    </TableCell>
                                    <TableCell>£{Number(exam.price || 0).toFixed(2)}</TableCell>
                                    <TableCell>
                                        {exam.isActive ? 'Published' : 'Draft'}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            onClick={() => navigate(`/admin/exams/${exam._id}/edit`)}
                                            color="primary"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleDelete(exam._id)}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Container>
    );
}

export default AdminExamsSimple; 